import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCompany } from '../contexts/CompanyContext';
import { completeLogin } from '../lib/api';
import config from '../config';

function YandexMark() {
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-[13px] font-bold leading-none">
      Я
    </span>
  );
}

const YANDEX_ERROR_MESSAGES = {
  yandex_denied: 'Вход через Яндекс отменён',
  yandex_invalid_request: 'Некорректный ответ от Яндекса — попробуйте снова',
  yandex_invalid_state: 'Сессия входа устарела — попробуйте снова',
  yandex_token_exchange_failed: 'Не удалось подтвердить вход через Яндекс',
  yandex_unavailable: 'Яндекс временно недоступен — попробуйте позже',
  yandex_profile_incomplete: 'Яндекс не предоставил email — проверьте разрешения приложения',
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  const navigate = useNavigate();
  const { selectCompany } = useCompany();
  const [searchParams] = useSearchParams();

  React.useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      toast.error(YANDEX_ERROR_MESSAGES[error] || 'Ошибка входа через Яндекс');
    }
  }, [searchParams]);

  const handleYandexLogin = () => {
    window.location.href = `${config.API_BASE}/auth/yandex/login`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${config.API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка входа');
      }

      const { access_token, user } = await response.json();
      const destination = await completeLogin(access_token, { selectCompany });

      toast.success(`Добро пожаловать, ${user.full_name || user.email}!`);
      navigate(destination);
    } catch (error) {
      toast.error(error.message || 'Ошибка входа в систему');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(ellipse_at_top,_theme(colors.blue.50),_theme(colors.slate.50)_60%)] px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-100 p-8 sm:p-10">
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 shadow-md shadow-blue-600/30">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              DataMetrics Cloud
            </h1>
            <p className="mt-1.5 text-sm text-gray-500">Система управления проектами</p>
          </div>

          <Button
            type="button"
            onClick={handleYandexLogin}
            className="w-full h-11 gap-2.5 bg-[#FC3F1D] hover:bg-[#e0350f] text-white font-medium shadow-sm"
            data-testid="yandex-login-button"
          >
            <YandexMark />
            Войти через Яндекс
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-medium uppercase tracking-wide text-slate-400">или</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          {!showEmailFallback ? (
            <button
              type="button"
              onClick={() => setShowEmailFallback(true)}
              className="group flex w-full items-center justify-center gap-1.5 text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors"
              data-testid="toggle-email-login"
            >
              Войти по email
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </button>
          ) : (
            <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-login-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="mt-1.5 h-10"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Пароль</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-1.5 h-10"
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Вход...' : 'Войти'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
